import { useRef, useState } from 'react'

import { isNullish } from '@apollo/client/cache/inmemory/helpers'
import { FinanceValueObjectsContactTypeEnum } from 'generated/resources/datatable.graphql'
import { ContactsImportTemplateDocument } from 'generated/resources/graphql'

import { ImportModal } from 'modules/core/components/ImportModal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { useMutation } from 'modules/core/hooks/useMutation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { downloadBlob } from 'modules/core/lib/download'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import handleResultWithFlash from 'modules/finance/lib/handleResultWithFlash'

import { useUploadContactsImportFile } from './useUploadContactsImportFile'

interface ContactsImportModalProps {
  onClose: () => void
  contactType: FinanceValueObjectsContactTypeEnum
}

export const ContactsImportModal = ({ onClose, contactType }: ContactsImportModalProps) => {
  const {
    loading: loadingUpload,
    rowsAdded,
    rowsUpdated,
    rowsToBeAdded,
    rowsToBeUpdated,
    errorChecks,
    errors,
    loadingCheck,
    uploadImportFileToCheck,
    uploadImportFile,
  } = useUploadContactsImportFile()
  const fileDropped = useRef<File | undefined>(undefined)
  const [fileName, setFileName] = useState<string | undefined>()
  const [showInfo, setShowInfo] = useState<boolean>(false)
  const [infoFileMessage, setInfoFileMessage] = useState<string[]>([])
  const [loadingMessage, setLoadingMessage] = useState<string>('')
  const [color, setColor] = useState<'positive' | 'destructive' | 'info'>('info')
  const {
    access: { locale },
  } = useCurrentSession()

  const [getFinanceContactImportTemplate] = useMutation(ContactsImportTemplateDocument, {
    extractorFn: (data) => data.financeMutations.getFinanceContactImportTemplate,
  })

  const downloadContactTemplate = async () => {
    const result = await getFinanceContactImportTemplate({ variables: { locale } })

    const handled = handleResult(result, handleResultWithFlash())

    if (handled.success) {
      if (!handled.data.contactImportTemplate) return
      const base64File = handled.data.contactImportTemplate.fileInBase64
      const fileType = handled.data.contactImportTemplate.fileType

      const fileResponse = await fetch(
        `data:${fileType};base64,${base64File.replace(/^data:.*,/, '')}`
      )

      const blob = await fileResponse.blob()
      downloadBlob(blob, handled.data.contactImportTemplate.fileName)
      track(trackingEvents.accounting.contacts.downloadTemplate)
    }
  }

  const onDropFile = async (files: File[]) => {
    const file = files[0]
    if (!file) {
      setInfoFileMessage([
        i18n.t('banking.import_transactions_modal.incorrect_file_format_message'),
      ])
      setColor('destructive')
      setShowInfo(true)
      trackUploadCheckError(contactType, 'no_file_or_bad_format')
      return
    }
    setLoadingMessage(i18n.t('banking.import_transactions_modal.checking_file_format'))

    setFileName(file.name)
    fileDropped.current = file
    const result = await uploadImportFileToCheck(file, contactType)
    const handled = handleResult(result, (error) =>
      trackUploadCheckError(contactType, error.trackError ?? '')
    )

    if (handled.success) {
      trackUploadCheckError(contactType, '')
    }
  }

  const onSubmit = async () => {
    if (!fileDropped.current || fileDropped.current === undefined) return null

    setLoadingMessage(i18n.t('banking.import_transactions_modal.spinner_message'))
    setShowInfo(false)
    if (contactType === 'vendor') track(trackingEvents.accounting.vendors.startImport)
    else if (contactType === 'client') track(trackingEvents.accounting.clients.startImport)
    else track(trackingEvents.accounting.contacts.startImport)

    const result = await uploadImportFile(fileDropped.current, contactType)
    const handled = handleResult(result, (errorResponse) => {
      app.setFlash({
        type: 'wrong',
        message: errorResponse.errors || i18n.t('banking.import_transactions_modal.failed_upload'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('banking.import_transactions_modal.successfull_upload'),
      })
      if (contactType === 'vendor') track(trackingEvents.accounting.vendors.confirmImport)
      else if (contactType === 'client') track(trackingEvents.accounting.clients.confirmImport)
      else track(trackingEvents.accounting.contacts.confirmImport)
    }
  }

  useUnsafeEffect(() => {
    if (errorChecks && errorChecks.length > 0) {
      setShowInfo(true)
      setColor('destructive')
      setInfoFileMessage(errorChecks)
    }
  }, [errorChecks])

  useUnsafeEffect(() => {
    if (errors && errors.length > 0) {
      setShowInfo(true)
      setColor('destructive')
      setInfoFileMessage(errors)
    }
  }, [errors])

  useEffect(() => {
    if (!isNullish(rowsToBeAdded) && !isNullish(rowsToBeUpdated)) {
      if (rowsToBeAdded === 0 && rowsToBeUpdated === 0) {
        setColor('info')
        setInfoFileMessage([
          i18n.t('banking.import_transactions_modal.file_ok_but_already_uploaded'),
        ])
      } else {
        setColor('positive')
        setInfoFileMessage(generate_info_message(contactType, rowsToBeAdded, rowsToBeUpdated))
      }
      setShowInfo(true)
    }
  }, [contactType, rowsToBeAdded, rowsToBeUpdated])

  useUnsafeEffect(() => {
    if (rowsAdded != null || rowsUpdated != null) {
      onClose()
      setFileName(undefined)
      setShowInfo(false)
      setInfoFileMessage([])
      setColor('destructive')
    }
  }, [rowsAdded, rowsUpdated, onClose])

  return (
    <ImportModal
      title={
        contactType === 'vendor'
          ? i18n.t('finance.vendors.import_modal.title')
          : i18n.t('finance.clients.import_modal.title')
      }
      description={
        contactType === 'vendor'
          ? i18n.t('finance.vendors.import_modal.description')
          : i18n.t('finance.clients.import_modal.description')
      }
      onClose={onClose}
      onSubmit={onSubmit}
      download={downloadContactTemplate}
      onDropFile={onDropFile}
      fileName={fileName}
      disabled={
        !fileName ||
        loadingCheck ||
        loadingUpload ||
        (errorChecks != null && errorChecks.length > 0)
      }
      loading={loadingCheck || loadingUpload}
      loadingMessage={loadingMessage}
      infoFileMessage={infoFileMessage}
      infoFileMessageColor={color}
      showInfo={showInfo}
    />
  )
}

function trackUploadCheckError(contactType: FinanceValueObjectsContactTypeEnum, error: string) {
  if (contactType === 'vendor')
    track(trackingEvents.accounting.vendors.uploadImportDocument, { error })
  else track(trackingEvents.accounting.clients.uploadImportDocument, { error })
}

function generate_info_message(
  contactType: FinanceValueObjectsContactTypeEnum,
  rowsToBeAdded: number,
  rowsToBeUpdated: number
): string[] {
  const contactAddedCopy =
    contactType === 'vendor'
      ? i18n.tp('finance.vendors.import_modal.rows_to_be_added', { count: rowsToBeAdded })
      : i18n.tp('finance.clients.import_modal.rows_to_be_added', { count: rowsToBeAdded })
  const contactUpdatedCopy =
    contactType === 'vendor'
      ? i18n.tp('finance.vendors.import_modal.rows_to_be_updated', { count: rowsToBeUpdated })
      : i18n.tp('finance.clients.import_modal.rows_to_be_updated', { count: rowsToBeUpdated })
  const contactAddedAndUpdatedParams = {
    addedCounter: rowsToBeAdded,
    updatedCounter: rowsToBeUpdated,
  }
  if (rowsToBeUpdated === 0) {
    return [contactAddedCopy]
  }
  if (rowsToBeAdded === 0) {
    return [contactUpdatedCopy]
  }
  if (rowsToBeAdded === 1 && rowsToBeUpdated === 1) {
    return [
      contactType === 'vendor'
        ? i18n.t(
            'finance.vendors.import_modal.rows_to_be_added_and_updated.one_one',
            contactAddedAndUpdatedParams
          )
        : i18n.t(
            'finance.clients.import_modal.rows_to_be_added_and_updated.one_one',
            contactAddedAndUpdatedParams
          ),
    ]
  } else if (rowsToBeAdded === 1 && rowsToBeUpdated > 1) {
    return [
      contactType === 'vendor'
        ? i18n.t(
            'finance.vendors.import_modal.rows_to_be_added_and_updated.one_other',
            contactAddedAndUpdatedParams
          )
        : i18n.t(
            'finance.clients.import_modal.rows_to_be_added_and_updated.one_other',
            contactAddedAndUpdatedParams
          ),
    ]
  } else if (rowsToBeAdded > 1 && rowsToBeUpdated === 1) {
    return [
      contactType === 'vendor'
        ? i18n.t(
            'finance.vendors.import_modal.rows_to_be_added_and_updated.other_one',
            contactAddedAndUpdatedParams
          )
        : i18n.t(
            'finance.clients.import_modal.rows_to_be_added_and_updated.other_one',
            contactAddedAndUpdatedParams
          ),
    ]
  } else {
    return [
      contactType === 'vendor'
        ? i18n.t(
            'finance.vendors.import_modal.rows_to_be_added_and_updated.other_other',
            contactAddedAndUpdatedParams
          )
        : i18n.t(
            'finance.clients.import_modal.rows_to_be_added_and_updated.other_other',
            contactAddedAndUpdatedParams
          ),
    ]
  }
}
